// Enhanced AI enhancement service for notification messages with contextual data
// This allows for data-aware AI feedback with COACH/EMPLOYER focus on program goals

import { buildEnhancementPrompt } from '../constants/aiPrompts';
import openaiService from './openai';
import ProgramContextService from './programContextService';

interface AIEnhancementRequest {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  context?: {
    action?: string; // e.g., 'save_draft', 'complete_activity', 'submit_form'
    data?: any; // The actual data that triggered the notification
    userType?: string; // 'participant', 'organizer', 'employer'
    adkType?: string; // 'ideate', 'pitch', etc.
    metadata?: Record<string, any>; // Additional context
    user?: {
      firstName?: string;
      lastName?: string;
      fullName?: string;
    };
    programContext?: {
      programId?: string;
      programName?: string;
      programDesc?: string;
      pathways?: string[];
      industries?: string[];
    };
  };
}

interface AIEnhancementResponse {
  enhancedMessage: string;
  success: boolean;
  error?: string;
  processingTime?: number;
  suggestions?: string[]; // AI-generated suggestions based on the data
}

class AIEnhancementService {
  // Analytics tracking
  private static enhancementStats = {
    totalRequests: 0,
    successfulEnhancements: 0,
    failedEnhancements: 0,
    averageProcessingTime: 0,
    totalProcessingTime: 0,
    dataAwareEnhancements: 0,
    programAwareEnhancements: 0,
    coachEmployerResponses: 0,
    creativeHumorResponses: 0
  };

  public static async enhanceNotification(
    request: AIEnhancementRequest
  ): Promise<AIEnhancementResponse> {
    const startTime = Date.now();
    this.enhancementStats.totalRequests++;

    try {
      const { message, type, context } = request;

      // Auto-include program context if user is in a program
      let enhancedContext = { ...context };
      if (ProgramContextService.isUserInProgram()) {
        const programContext = ProgramContextService.getCurrentProgramContext();
        enhancedContext = {
          ...context,
          programContext: {
            programId: programContext.programId,
            programName: programContext.programName,
            programDesc: programContext.programDesc,
            pathways: programContext.pathways,
            industries: programContext.industries
          }
        };
        this.enhancementStats.programAwareEnhancements++;
        this.enhancementStats.coachEmployerResponses++;
      }

      // Create a context-aware prompt based on notification type and data
      // Use centralized prompt builder with COACH/EMPLOYER focus
      const prompt = buildEnhancementPrompt(message, type, enhancedContext);

      // Debug logging to see the actual prompt
      console.log('🔍 AI Enhancement Debug - Full Prompt:', prompt);
      console.log('🔍 AI Enhancement Debug - Enhanced Context:', enhancedContext);
      console.log(
        '🔍 AI Enhancement Debug - COACH/EMPLOYER Mode:',
        !!enhancedContext?.programContext
      );

      const enhancedMessage = await openaiService.generateScope(prompt);

      // Remove quotation marks from the response
      let cleanMessage = enhancedMessage.replace(/^["']|["']$/g, '');

      // Post-process to replace any placeholders with actual user names
      if (enhancedContext?.user) {
        const { user } = enhancedContext;
        const userName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim();

        if (userName) {
          // Replace common placeholders with actual name
          cleanMessage = cleanMessage
            .replace(/\[NAME\]/gi, userName)
            .replace(/\[participant\]/gi, userName)
            .replace(/\[user\]/gi, userName)
            .replace(/\[student\]/gi, userName)
            .replace(/\[learner\]/gi, userName)
            .replace(/\[member\]/gi, userName);

          console.log('🔍 AI Enhancement Debug - Post-processed message:', cleanMessage);
        }
      }

      // Check for inappropriate language and enhance with creative humor
      const hasInappropriateLanguage = this.detectInappropriateLanguage(message);
      if (hasInappropriateLanguage) {
        console.log(
          '🔍 AI Enhancement: Inappropriate language detected, applying creative humor response'
        );
        this.enhancementStats.creativeHumorResponses =
          (this.enhancementStats.creativeHumorResponses || 0) + 1;
      }

      // Validate that response focuses on program goals and includes validation indicators
      if (enhancedContext?.programContext) {
        const validation = this.validateProgramGoalFocus(
          cleanMessage,
          enhancedContext.programContext
        );

        if (!validation.hasProgramFocus) {
          console.warn('🔍 AI Enhancement Warning: Response may lack program goal focus');
        }

        if (!validation.hasValidationIndicators) {
          console.warn('🔍 AI Enhancement Warning: Response may lack validation indicators');
        }

        console.log('🔍 AI Enhancement Validation:', {
          hasProgramFocus: validation.hasProgramFocus,
          hasValidationIndicators: validation.hasValidationIndicators,
          validationType: validation.validationType,
          hasInappropriateLanguage
        });
      }

      const processingTime = Date.now() - startTime;
      this.enhancementStats.successfulEnhancements++;
      this.enhancementStats.totalProcessingTime += processingTime;
      this.enhancementStats.averageProcessingTime =
        this.enhancementStats.totalProcessingTime / this.enhancementStats.successfulEnhancements;

      // Log analytics in production
      if (process.env.NODE_ENV === 'production') {
        console.log('🔍 AI Enhancement Analytics:', {
          type,
          hasContext: !!enhancedContext,
          hasProgramContext: !!enhancedContext?.programContext,
          isCoachEmployer: !!enhancedContext?.programContext,
          action: enhancedContext?.action,
          adkType: enhancedContext?.adkType,
          originalLength: message.length,
          enhancedLength: cleanMessage.length,
          processingTime: `${processingTime}ms`,
          success: true
        });
      }

      return {
        enhancedMessage: cleanMessage,
        success: true,
        processingTime
      };
    } catch (error) {
      const processingTime = Date.now() - startTime;
      this.enhancementStats.failedEnhancements++;

      // Log errors in production
      if (process.env.NODE_ENV === 'production') {
        console.error('🔍 AI Enhancement Error:', {
          type: request.type,
          action: request.context?.action,
          error: error instanceof Error ? error.message : 'Unknown error',
          processingTime: `${processingTime}ms`,
          success: false
        });
      }

      return {
        enhancedMessage: request.message, // Fallback to original message
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        processingTime
      };
    }
  }

  // Get analytics for monitoring
  public static getAnalytics() {
    return {
      ...this.enhancementStats,
      successRate:
        this.enhancementStats.totalRequests > 0
          ? `${(
              (this.enhancementStats.successfulEnhancements / this.enhancementStats.totalRequests) *
              100
            ).toFixed(2)}%`
          : '0%',
      dataAwareRate:
        this.enhancementStats.totalRequests > 0
          ? `${(
              (this.enhancementStats.dataAwareEnhancements / this.enhancementStats.totalRequests) *
              100
            ).toFixed(2)}%`
          : '0%',
      programAwareRate:
        this.enhancementStats.totalRequests > 0
          ? `${(
              (this.enhancementStats.programAwareEnhancements /
                this.enhancementStats.totalRequests) *
              100
            ).toFixed(2)}%`
          : '0%',
      coachEmployerRate:
        this.enhancementStats.totalRequests > 0
          ? `${(
              (this.enhancementStats.coachEmployerResponses / this.enhancementStats.totalRequests) *
              100
            ).toFixed(2)}%`
          : '0%'
    };
  }

  // Reset analytics (useful for testing)
  public static resetAnalytics() {
    this.enhancementStats = {
      totalRequests: 0,
      successfulEnhancements: 0,
      failedEnhancements: 0,
      averageProcessingTime: 0,
      totalProcessingTime: 0,
      dataAwareEnhancements: 0,
      programAwareEnhancements: 0,
      coachEmployerResponses: 0,
      creativeHumorResponses: 0
    };
  }

  // Detect inappropriate language in messages
  private static detectInappropriateLanguage(message: string): boolean {
    if (!message) return false;

    const messageLower = message.toLowerCase();

    // Common inappropriate words (keeping it professional)
    const inappropriateWords = [
      'damn',
      'hell',
      'crap',
      'suck',
      'sucks',
      'sucking',
      'stupid',
      'idiot',
      'moron',
      'dumb',
      'dumbass',
      'hate',
      'hates',
      'hating',
      'terrible',
      'awful',
      'ridiculous',
      'nonsense',
      'bull',
      'bs',
      'b.s.'
    ];

    return inappropriateWords.some(word => messageLower.includes(word));
  }

  // Validate program goal focus and validation indicators in responses
  public static validateProgramGoalFocus(
    message: string,
    programContext?: any
  ): {
    hasProgramFocus: boolean;
    hasValidationIndicators: boolean;
    validationType?: 'on-track' | 'partial' | 'off-track' | 'innovative' | 'uncertain';
  } {
    if (!programContext) {
      return { hasProgramFocus: true, hasValidationIndicators: false };
    }

    const programName = programContext.programName?.toLowerCase() || '';
    const programDesc = programContext.programDesc?.toLowerCase() || '';

    const messageLower = message.toLowerCase();

    // Check for program goal focus indicators
    const hasProgramName = programName && messageLower.includes(programName);
    const hasProgramMention =
      messageLower.includes('program') ||
      messageLower.includes('goal') ||
      messageLower.includes('mission');
    const hasObjectiveFocus =
      messageLower.includes('objective') ||
      messageLower.includes('advance') ||
      messageLower.includes('complete');

    const hasProgramFocus = hasProgramName || hasProgramMention || hasObjectiveFocus;

    // Check for validation indicators
    const hasValidationIndicators =
      messageLower.includes('✅') ||
      messageLower.includes('⚠️') ||
      messageLower.includes('❌') ||
      messageLower.includes('🎯') ||
      messageLower.includes('❓') ||
      messageLower.includes('on-track') ||
      messageLower.includes('off-track') ||
      messageLower.includes('partial') ||
      messageLower.includes('innovative') ||
      messageLower.includes('uncertain') ||
      messageLower.includes('validates') ||
      messageLower.includes('validation');

    // Determine validation type
    let validationType:
      | 'on-track'
      | 'partial'
      | 'off-track'
      | 'innovative'
      | 'uncertain'
      | undefined;

    if (messageLower.includes('✅') || messageLower.includes('on-track')) {
      validationType = 'on-track';
    } else if (messageLower.includes('⚠️') || messageLower.includes('partial')) {
      validationType = 'partial';
    } else if (messageLower.includes('❌') || messageLower.includes('off-track')) {
      validationType = 'off-track';
    } else if (messageLower.includes('🎯') || messageLower.includes('innovative')) {
      validationType = 'innovative';
    } else if (
      messageLower.includes('❓') ||
      messageLower.includes('uncertain') ||
      messageLower.includes('?')
    ) {
      validationType = 'uncertain';
    }

    return {
      hasProgramFocus,
      hasValidationIndicators,
      validationType
    };
  }
}

// Default export
export default AIEnhancementService;
